import { useCallback } from 'react';

// 将 lastReportTimeMap 提升到模块级别，让所有组件实例共享同一个 Map
// 这样可以确保相同的 ID 在所有组件中共享节流状态，避免重复上报
const lastReportTimeMap = new Map<string, number>();
// 存储每个 ID 的定时器，用于延迟执行第二次上报
const timerMap = new Map<string, NodeJS.Timeout>();

/**
 * 一个基于模块级 Map 的节流 Hooks，用于处理不同 ID 的独立节流。
 * 所有组件实例共享同一个节流状态，确保相同的 ID 不会重复上报。
 * 逻辑：第一次调用立即执行，然后设置定时器在 wait 时间后自动执行第二次。
 * 后续调用按 wait 时间节流。
 * @param {object} options
 * @param {number} [options.wait=60000] - 节流等待时间，单位毫秒
 * @returns {function} throttleFn - 一个节流函数，它接收 ID 和上报逻辑作为参数
 */
export function useThrottleByRef({ wait = 60000 } = {}) {
  // 使用 useCallback 确保返回的函数引用稳定
  const throttleFn = useCallback((id: string | number, callback: () => void) => {
    // 检查 ID 是否有效
    if (!id) {
      console.warn('Tracking ID cannot be empty');
      return;
    }

    const idKey = String(id);
    const lastTime = lastReportTimeMap.get(idKey) || 0;
    const currentTime = Date.now();

    // 如果 lastTime 为 0，说明是第一次调用，立即执行并设置定时器
    if (lastTime === 0) {
      // 立即执行第一次上报
      callback();
      // 更新该 ID 的上一次上报时间
      lastReportTimeMap.set(idKey, currentTime);

      // 清除之前的定时器（如果有）
      const existingTimer = timerMap.get(idKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // 设置 wait 时间后的第二次上报
      const timer = setTimeout(() => {
        callback();
        lastReportTimeMap.set(idKey, Date.now());
        timerMap.delete(idKey);
      }, wait);
      timerMap.set(idKey, timer);
    } else if (currentTime - lastTime >= wait) {
      // 后续调用，检查是否超过 wait 时间
      // 清除之前的定时器（如果有）
      const existingTimer = timerMap.get(idKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
        timerMap.delete(idKey);
      }

      // 触发回调，执行上报逻辑
      callback();
      // 更新该 ID 的上一次上报时间
      lastReportTimeMap.set(idKey, currentTime);
    }
  }, [wait]);

  return throttleFn;
}